import { getNumber } from "./validation";
import { Money } from "./Money";
import {
  Cadence,
  RechargeItemFormatted,
  RechargeItemRaw,
  ChargeLineItem,
} from "@/features/portal";

window.ElevarDataLayer = window.ElevarDataLayer ?? [];

/*
How To name events:
Category: The name of the module or page type (Cart, Nav, Carousel, PDP, Lander)
Action: The action being taken (Added Recommended Item, Increase Item Quantity) avoid simple actions like "clicked" 
Label: (optional) The text on the button, destination url, any additional info (www.dropps.com/, submit, unscented).
You can use '|' to separate information like 'name|id' but do so sparingly.  
Link to naming docs: https://docs.google.com/document/d/1bxyqzZ7iKr7XuAxEFEup76ap3DXmvlOqjGxz46wRb94/edit?usp=sharing
*/

/* 
  06/29/24 Addison Applications
  Updated for elevar source upgrade. Prevously events from this file were being 
  submitted directly to gtag and not through elevar. This file is now updated to
  submit events through elevar.

  The source updgrade requires Google Tag Manager to be completely removed from the
  site since elevar is now implmented through Shopify pixels., so making calls to gtag will no longer work.
*/

// replicating the data object created by elevar as best we can
// missing item_category, and sku because we don't always have that info
type AnalyticsItem = {
  item_variant_id: string;
  item_product_id: string;
  item_name: string;
  item_variant: string;
  quantity: string;
  price: string;
  is_subscription: boolean;
};

export type TrackEvent =
  | {
      name: "Event";
      eventName: string;
      params?: Record<string, string | number>;
    }
  | {
      name: "Add Item Info";
      sourceModule: string;
    }
  | {
      name: "Update Cart Total";
      total: Money;
    }
  | {
      name: "Portal Update Address";
    }
  | {
      name: "Portal Adjust Quantity";
      item: RechargeItemRaw;
      previousQuantity: number;
    }
  | {
      name: "Portal Update Ship Date";
      item: RechargeItemRaw;
      previousDate: Date;
    }
  | {
      name: "Portal Update Subscription Cadence";
      item: RechargeItemRaw;
      previousCadence: Cadence;
    }
  | {
      name: "Portal Swap Item";
      prevItem: RechargeItemFormatted;
      item: RechargeItemRaw;
    }
  | {
      name: "Portal Cancel Order";
      item: RechargeItemFormatted;
    }
  | {
      name: "Portal Skip Order";
      items: ChargeLineItem[] | RechargeItemFormatted[];
    }
  | {
      name: "Portal Order Now";
      items: ChargeLineItem[];
    }
  | {
      name: "Portal Reactivate Subscription";
      item: RechargeItemRaw;
    }
  | {
      name: "Portal Add Discount";
      discount: string;
    }
  | {
      name: "Portal Remove Discount";
    }
  | {
      name: "Portal Add Item";
      item: RechargeItemRaw;
      sourceModule: string;
    };

export async function track(event: TrackEvent) {
  if (!window.ElevarDataLayer) {
    return;
  }

  switch (event.name) {
    case "Event": {
      window.ElevarDataLayer.push({
        event: event.eventName,
        eventModel: event.params,
      });
      return;
    }
    case "Add Item Info": {
      window.ElevarDataLayer.push({
        source_module: event.sourceModule,
      });
      return;
    }

    case "Update Cart Total": {
      window.ElevarDataLayer.push({
        event: "cart_update_total",
        cart_total: event.total.asDollars().toString(),
      });
      return;
    }
    case "Portal Update Address": {
      window.ElevarDataLayer.push({
        event: "portal_update_address",
      });
      return;
    }
    case "Portal Adjust Quantity": {
      const item = rechargeSubscriptionToAnalyticsItem(event.item);
      window.ElevarDataLayer.push({
        event: "portal_adjust_quantity",
        item,
        quantity: event.item.quantity,
        previous_quantity: event.previousQuantity,
        is_subscription: event.item.status === "ACTIVE",
      });
      return;
    }
    case "Portal Update Ship Date": {
      if (!event.item.next_charge_scheduled_at) {
        return;
      }
      const item = rechargeSubscriptionToAnalyticsItem(event.item);
      const newDate = new Date(event.item.next_charge_scheduled_at);
      const daysAdjusted =
        (newDate.getTime() - event.previousDate.getTime()) / (1000 * 3600 * 24);
      const action =
        daysAdjusted < 0
          ? "moved subscription sooner"
          : "moved subscription later";
      window.ElevarDataLayer.push({
        event: "portal_update_ship_date",
        item,
        date: newDate.toISOString(),
        days_adjusted: Math.round(Math.abs(daysAdjusted)),
        action,
        is_subscription: event.item.status === "ACTIVE",
      });
      return;
    }
    case "Portal Update Subscription Cadence": {
      const item = rechargeSubscriptionToAnalyticsItem(event.item);
      window.ElevarDataLayer.push({
        event: "portal_update_subscription_cadence",
        item,
        cadence: getNumber(event.item.order_interval_frequency),
        previous_cadence: event.previousCadence.frequency,
      });
      return;
    }
    case "Portal Swap Item": {
      const item = rechargeSubscriptionToAnalyticsItem(event.item);
      const prevItem = rechargeSubscriptionToAnalyticsItem(event.prevItem);
      window.ElevarDataLayer.push({
        event: "portal_swap_item",

        item,
        previous_item: prevItem,
      });
      return;
    }
    case "Portal Cancel Order": {
      const item = rechargeSubscriptionToAnalyticsItem(event.item);
      window.ElevarDataLayer.push({
        event: "portal_cancel_order",

        item,
        is_subscription: event.item.status === "ACTIVE",
      });
      return;
    }
    case "Portal Skip Order": {
      const { items } = event || {};
      const analyticsItems = items.map((item) =>
        rechargeSubscriptionToAnalyticsItem(item)
      );
      window.ElevarDataLayer.push({
        event: "portal_skip_order",

        items: analyticsItems,
      });
      return;
    }
    case "Portal Order Now": {
      const { items } = event || {};
      const analyticsItems = items.map((item) =>
        rechargeSubscriptionToAnalyticsItem(item)
      );
      window.ElevarDataLayer.push({
        event: "portal_order_now",

        items: analyticsItems,
      });
      return;
    }
    case "Portal Reactivate Subscription": {
      const item = rechargeSubscriptionToAnalyticsItem(event.item);
      window.ElevarDataLayer.push({
        event: "portal_reactivate_subscription",

        item,
      });
      return;
    }
    case "Portal Add Discount": {
      window.ElevarDataLayer.push({
        event: "portal_add_discount",

        discount: event.discount,
      });
      return;
    }
    case "Portal Remove Discount": {
      window.ElevarDataLayer.push({
        event: "portal_remove_discount",
      });
      return;
    }
    case "Portal Add Item": {
      const item = rechargeSubscriptionToAnalyticsItem(event.item);
      const interval = event.item.order_interval_frequency;
      window.ElevarDataLayer.push({
        event: "portal_add_item",

        item,
        ...(interval && { interval }),
        source_module: event.sourceModule,
        is_subscription: event.item.status === "ACTIVE",
      });
    }
    default:
  }
}

const rechargeSubscriptionToAnalyticsItem = (
  sub: RechargeItemRaw | RechargeItemFormatted | ChargeLineItem
): AnalyticsItem => {
  let itemName = "";
  if ("product_title" in sub) {
    // sub is RechargeItemFormatted or RechargeItemRaw
    itemName = sub.product_title;
  } else {
    // sub is ChargeLineItem
    itemName = sub.title;
  }
  let isSubscription: boolean;
  if ("status" in sub) {
    isSubscription = sub.status === "ACTIVE" ? true : false;
  } else if ("type" in sub) {
    isSubscription = sub.type === "SUBSCRIPTION" ? true : false;
  } else {
    isSubscription = false;
  }
  return {
    item_name: itemName,
    item_product_id: sub.shopify_product_id.toString(10),
    item_variant: sub.variant_title,
    item_variant_id: sub.shopify_variant_id.toString(10),
    quantity: sub.quantity.toString(10),
    price:
      typeof sub.price === "number"
        ? Money.from(sub.price, "$").format()
        : typeof sub.price === "string"
        ? Money.from(parseFloat(sub.price), "$").format()
        : sub.price.format(),
    is_subscription: isSubscription,
  };
};
